import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Types for analysis data (matching the AnalysisResult interface)
interface PlatformAnalysis {
  complianceScore: number;
  performanceScore: number;
  status: string;
  predictedCTR: number;
  predictedEngagement: number;
  algorithmFavorability: number;
  issues: Array<{
    severity: 'low' | 'medium' | 'high' | 'critical';
    title: string;
    description: string;
    rule: string;
    recommendation: string;
  }>;
}

interface PerformanceAnalysis {
  overallPerformanceScore: number;
  brandAwarenessScore: number;
  keyFactors: {
    attention: {
      score: number;
      description: string;
      details: string[];
    };
    clarity: {
      score: number;
      description: string;
      details: string[];
    };
    engagement: {
      score: number;
      description: string;
      details: string[];
    };
    branding?: {
      score: number;
      description: string;
      details: string[];
    };
  };
  visualAnalysis?: {
    visualScore: number;
    colorScore: number;
    layoutScore: number;
    typographyScore: number;
    audienceAlignment: number;
    designInsights: string[];
    visualRecommendations: string[];
  };
  attentionHeatmap?: {
    hotspots: Array<{
      x: number;
      y: number;
      intensity: number;
      element: string;
    }>;
  };
}

interface AIRecommendation {
  category: string;
  priority: 'low' | 'medium' | 'high';
  title: string;
  description: string;
  impact: string;
  platforms: string[];
}

// Project export interfaces
interface ProjectMemberInfo {
  userId: string;
  name: string;
  email: string;
  role: string;
  addedAt: string;
}

interface CreativeFamily {
  rootCreative: {
    id: string;
    name: string;
    type: 'image' | 'video';
    url: string;
    thumbnail?: string;
    analysis?: any;
  };
  versions: Array<{
    id: string;
    name: string;
    type: 'image' | 'video';
    url: string;
    thumbnail?: string;
    version_number?: number;
    is_current_version: boolean;
    analysis?: any;
    created_at: string;
  }>;
  currentVersion: any;
  totalVersions: number;
}

interface ProjectStatistics {
  overall: {
    averageComplianceScore: number;
    averagePerformanceScore: number;
    totalIssues: number;
    criticalIssues: number;
    analyzedCreatives: number;
    topPerformingPlatform: string;
    worstPerformingPlatform: string;
  };
  platforms: Record<string, {
    averageComplianceScore: number;
    averagePerformanceScore: number;
    totalCreatives: number;
    issuesCount: number;
    criticalIssuesCount: number;
  }>;
  trends: {
    complianceImprovement: number;
    performanceImprovement: number;
    recentAnalyses: number;
  };
}

interface ProjectExportData {
  id: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
  status: string;
  members: ProjectMemberInfo[];
  creativeFamilies: CreativeFamily[];
  statistics: ProjectStatistics;
  totalCreatives: number;
  totalAnalyses: number;
}

interface AnalysisData {
  id: string;
  creativeName: string;
  creativeType: 'image' | 'video';
  creativeUrl: string;
  analysisDate: string;
  overallScore: number;
  creditsUsed: number;
  version: string;
  platforms: Record<string, PlatformAnalysis>;
  performanceAnalysis: PerformanceAnalysis;
  aiRecommendations: AIRecommendation[];
}

export class ExportService {
  /**
   * Export project data as PDF
   */
  static async exportProjectToPDF(projectData: ProjectExportData): Promise<void> {
    try {
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let currentY = 20;

      // Helper function to add a new page if needed
      const checkPageBreak = (neededHeight: number) => {
        if (currentY + neededHeight > pageHeight - 20) {
          pdf.addPage();
          currentY = 20;
        }
      };

      // Helper function to wrap text
      const addWrappedText = (text: string, x: number, y: number, maxWidth: number, fontSize: number = 12) => {
        pdf.setFontSize(fontSize);
        const lines = pdf.splitTextToSize(text, maxWidth);
        pdf.text(lines, x, y);
        return y + (lines.length * fontSize * 0.35);
      };

      // Page 1: Cover Page
      pdf.setFontSize(28);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Project Report', pageWidth / 2, currentY, { align: 'center' });
      currentY += 20;

      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'normal');
      pdf.text(projectData.name, pageWidth / 2, currentY, { align: 'center' });
      currentY += 15;

      if (projectData.description) {
        pdf.setFontSize(12);
        currentY = addWrappedText(projectData.description, pageWidth / 2 - 80, currentY, 160);
        currentY += 10;
      }

      // Project Statistics Overview
      const centerX = pageWidth / 2;
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Project Overview', centerX, currentY, { align: 'center' });
      currentY += 15;

      // Statistics boxes
      const boxWidth = 35;
      const boxHeight = 25;
      const startX = centerX - (boxWidth * 2);
      
      // Overall Score box
      pdf.setDrawColor(147, 51, 234);
      pdf.setLineWidth(1);
      pdf.rect(startX, currentY, boxWidth, boxHeight);
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      const avgScore = Math.round((projectData.statistics.overall.averageComplianceScore + projectData.statistics.overall.averagePerformanceScore) / 2);
      pdf.text(`${avgScore}%`, startX + boxWidth/2, currentY + boxHeight/2 + 3, { align: 'center' });
      pdf.setFontSize(8);
      pdf.text('Avg Score', startX + boxWidth/2, currentY + boxHeight/2 + 12, { align: 'center' });
      
      // Analyzed Creatives box
      pdf.rect(startX + boxWidth + 5, currentY, boxWidth, boxHeight);
      pdf.setFontSize(18);
      pdf.text(String(projectData.statistics.overall.analyzedCreatives), startX + boxWidth + 5 + boxWidth/2, currentY + boxHeight/2 + 3, { align: 'center' });
      pdf.setFontSize(8);
      pdf.text('Analyzed', startX + boxWidth + 5 + boxWidth/2, currentY + boxHeight/2 + 12, { align: 'center' });
      
      // Critical Issues box
      pdf.rect(startX + (boxWidth + 5) * 2, currentY, boxWidth, boxHeight);
      pdf.setFontSize(18);
      pdf.text(String(projectData.statistics.overall.criticalIssues), startX + (boxWidth + 5) * 2 + boxWidth/2, currentY + boxHeight/2 + 3, { align: 'center' });
      pdf.setFontSize(8);
      pdf.text('Critical Issues', startX + (boxWidth + 5) * 2 + boxWidth/2, currentY + boxHeight/2 + 12, { align: 'center' });

      currentY += boxHeight + 20;

      // Project Details
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Project Details', 20, currentY);
      currentY += 10;

      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Created: ${new Date(projectData.created_at).toLocaleDateString()}`, 20, currentY);
      currentY += 8;
      pdf.text(`Last Updated: ${new Date(projectData.updated_at).toLocaleDateString()}`, 20, currentY);
      currentY += 8;
      pdf.text(`Status: ${projectData.status}`, 20, currentY);
      currentY += 8;
      pdf.text(`Total Creatives: ${projectData.totalCreatives}`, 20, currentY);
      currentY += 8;
      pdf.text(`Total Analyses: ${projectData.totalAnalyses}`, 20, currentY);
      currentY += 8;
      pdf.text(`Team Members: ${projectData.members.length}`, 20, currentY);
      currentY += 15;

      // Platform Performance
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Platform Performance', 20, currentY);
      currentY += 10;

      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Top Performing: ${projectData.statistics.overall.topPerformingPlatform}`, 20, currentY);
      currentY += 8;
      if (projectData.statistics.overall.worstPerformingPlatform !== projectData.statistics.overall.topPerformingPlatform) {
        pdf.text(`Needs Improvement: ${projectData.statistics.overall.worstPerformingPlatform}`, 20, currentY);
        currentY += 8;
      }

      // Page 2: Detailed Statistics
      pdf.addPage();
      currentY = 20;

      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Detailed Statistics', 20, currentY);
      currentY += 15;

      // Overall Statistics
      pdf.setFontSize(16);
      pdf.text('Overall Performance', 20, currentY);
      currentY += 10;

      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Average Compliance Score: ${projectData.statistics.overall.averageComplianceScore}%`, 25, currentY);
      currentY += 8;
      pdf.text(`Average Performance Score: ${projectData.statistics.overall.averagePerformanceScore}%`, 25, currentY);
      currentY += 8;
      pdf.text(`Total Issues: ${projectData.statistics.overall.totalIssues}`, 25, currentY);
      currentY += 8;
      pdf.text(`Critical Issues: ${projectData.statistics.overall.criticalIssues}`, 25, currentY);
      currentY += 15;

      // Platform-specific Statistics
      if (Object.keys(projectData.statistics.platforms).length > 0) {
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Platform-Specific Performance', 20, currentY);
        currentY += 10;

        Object.entries(projectData.statistics.platforms).forEach(([platform, stats]) => {
          checkPageBreak(40);
          
          pdf.setFontSize(14);
          pdf.text(platform, 25, currentY);
          currentY += 8;

          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'normal');
          pdf.text(`  Compliance: ${Math.round(stats.averageComplianceScore)}%`, 30, currentY);
          currentY += 6;
          pdf.text(`  Performance: ${Math.round(stats.averagePerformanceScore)}%`, 30, currentY);
          currentY += 6;
          pdf.text(`  Creatives: ${stats.totalCreatives}`, 30, currentY);
          currentY += 6;
          pdf.text(`  Issues: ${stats.issuesCount} (${stats.criticalIssuesCount} critical)`, 30, currentY);
          currentY += 10;
        });
      }

      // Page 3: Creative Families
      pdf.addPage();
      currentY = 20;

      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Creative Families', 20, currentY);
      currentY += 15;

      projectData.creativeFamilies.slice(0, 10).forEach((family, index) => {
        checkPageBreak(50);
        
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${index + 1}. ${family.rootCreative.name}`, 25, currentY);
        currentY += 8;

        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Type: ${family.rootCreative.type}`, 30, currentY);
        currentY += 6;
        pdf.text(`Versions: ${family.totalVersions}`, 30, currentY);
        currentY += 6;
        pdf.text(`Current Version: ${family.currentVersion?.version_number || 1}`, 30, currentY);
        currentY += 6;
        
        if (family.currentVersion?.analysis) {
          const analysis = family.currentVersion.analysis;
          if (analysis.platforms) {
            const platformNames = Object.keys(analysis.platforms);
            pdf.text(`Platforms: ${platformNames.join(', ')}`, 30, currentY);
            currentY += 6;
            
            // Show average scores
            const avgCompliance = Object.values(analysis.platforms).reduce((sum: number, platform: any) => sum + (platform.complianceScore || 0), 0) / platformNames.length;
            const avgPerformance = Object.values(analysis.platforms).reduce((sum: number, platform: any) => sum + (platform.performanceScore || 0), 0) / platformNames.length;
            
            pdf.text(`Avg Compliance: ${Math.round(avgCompliance)}%`, 30, currentY);
            currentY += 6;
            pdf.text(`Avg Performance: ${Math.round(avgPerformance)}%`, 30, currentY);
            currentY += 6;
          }
        }
        
        currentY += 8;
      });

      if (projectData.creativeFamilies.length > 10) {
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'italic');
        pdf.text(`... and ${projectData.creativeFamilies.length - 10} more creative families`, 25, currentY);
      }

      // Page 4: Team Members
      if (projectData.members.length > 0) {
        pdf.addPage();
        currentY = 20;

        pdf.setFontSize(20);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Team Members', 20, currentY);
        currentY += 15;

        projectData.members.forEach((member, index) => {
          checkPageBreak(25);
          
          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.text(`${index + 1}. ${member.name}`, 25, currentY);
          currentY += 8;

          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'normal');
          pdf.text(`Email: ${member.email}`, 30, currentY);
          currentY += 6;
          pdf.text(`Role: ${member.role}`, 30, currentY);
          currentY += 6;
          pdf.text(`Added: ${new Date(member.addedAt).toLocaleDateString()}`, 30, currentY);
          currentY += 10;
        });
      }

      // Footer on each page
      const totalPages = pdf.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Generated by Rulevia - ${new Date().toLocaleDateString()}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth - 20, pageHeight - 10, { align: 'right' });
      }

      // Generate filename
      const fileName = `${projectData.name.replace(/[^a-zA-Z0-9]/g, '_')}_project_report_${new Date().toISOString().split('T')[0]}.pdf`;
      
      // Save the PDF
      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating project PDF:', error);
      throw new Error('Failed to generate project PDF report');
    }
  }

  /**
   * Export project data as CSV
   */
  static exportProjectToCSV(projectData: ProjectExportData): void {
    try {
      const csvRows: string[] = [];
      
      // Project Information Section
      csvRows.push('PROJECT INFORMATION');
      csvRows.push('Field,Value');
      csvRows.push(`"Name","${projectData.name || 'Unknown'}"`);
      csvRows.push(`"Description","${(projectData.description || '').replace(/"/g, '""')}"`);
      csvRows.push(`"Created","${new Date(projectData.created_at).toLocaleDateString()}"`);
      csvRows.push(`"Last Updated","${new Date(projectData.updated_at).toLocaleDateString()}"`);
      csvRows.push(`"Status","${projectData.status}"`);
      csvRows.push(`"Total Creatives","${projectData.totalCreatives}"`);
      csvRows.push(`"Total Analyses","${projectData.totalAnalyses}"`);
      csvRows.push(`"Team Members","${projectData.members.length}"`);
      csvRows.push('');
      
      // Overall Statistics
      csvRows.push('OVERALL STATISTICS');
      csvRows.push('Metric,Value');
      csvRows.push(`"Average Compliance Score","${projectData.statistics.overall.averageComplianceScore}%"`);
      csvRows.push(`"Average Performance Score","${projectData.statistics.overall.averagePerformanceScore}%"`);
      csvRows.push(`"Total Issues","${projectData.statistics.overall.totalIssues}"`);
      csvRows.push(`"Critical Issues","${projectData.statistics.overall.criticalIssues}"`);
      csvRows.push(`"Analyzed Creatives","${projectData.statistics.overall.analyzedCreatives}"`);
      csvRows.push(`"Top Performing Platform","${projectData.statistics.overall.topPerformingPlatform}"`);
      csvRows.push(`"Worst Performing Platform","${projectData.statistics.overall.worstPerformingPlatform}"`);
      csvRows.push('');
      
      // Platform Statistics
      if (Object.keys(projectData.statistics.platforms).length > 0) {
        csvRows.push('PLATFORM STATISTICS');
        csvRows.push('Platform,Avg Compliance Score,Avg Performance Score,Total Creatives,Issues Count,Critical Issues');
        Object.entries(projectData.statistics.platforms).forEach(([platform, stats]) => {
          csvRows.push([
            `"${platform}"`,
            Math.round(stats.averageComplianceScore).toString(),
            Math.round(stats.averagePerformanceScore).toString(),
            stats.totalCreatives.toString(),
            stats.issuesCount.toString(),
            stats.criticalIssuesCount.toString()
          ].join(','));
        });
        csvRows.push('');
      }
      
      // Creative Families
      csvRows.push('CREATIVE FAMILIES');
      csvRows.push('Family Name,Type,Total Versions,Current Version,Avg Compliance,Avg Performance');
      projectData.creativeFamilies.forEach((family) => {
        let avgCompliance = 0;
        let avgPerformance = 0;
        
        if (family.currentVersion?.analysis?.platforms) {
          const platforms = Object.values(family.currentVersion.analysis.platforms);
          avgCompliance = platforms.reduce((sum: number, platform: any) => sum + (platform.complianceScore || 0), 0) / platforms.length;
          avgPerformance = platforms.reduce((sum: number, platform: any) => sum + (platform.performanceScore || 0), 0) / platforms.length;
        }
        
        csvRows.push([
          `"${family.rootCreative.name}"`,
          `"${family.rootCreative.type}"`,
          family.totalVersions.toString(),
          (family.currentVersion?.version_number || 1).toString(),
          Math.round(avgCompliance).toString(),
          Math.round(avgPerformance).toString()
        ].join(','));
      });
      csvRows.push('');
      
      // Team Members
      if (projectData.members.length > 0) {
        csvRows.push('TEAM MEMBERS');
        csvRows.push('Name,Email,Role,Added Date');
        projectData.members.forEach((member) => {
          csvRows.push([
            `"${member.name}"`,
            `"${member.email}"`,
            `"${member.role}"`,
            `"${new Date(member.addedAt).toLocaleDateString()}"`
          ].join(','));
        });
      }

      // Create and download CSV
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        const fileName = `${projectData.name.replace(/[^a-zA-Z0-9]/g, '_')}_project_export_${new Date().toISOString().split('T')[0]}.csv`;
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error generating project CSV:', error);
      throw new Error('Failed to generate project CSV export');
    }
  }

  /**
   * Export project data as JSON
   */
  static exportProjectToJSON(projectData: ProjectExportData): void {
    try {
      // Create a clean JSON structure
      const exportData = {
        ...projectData,
        exportMetadata: {
          exportDate: new Date().toISOString(),
          exportedBy: 'Rulevia Project Platform',
          version: '1.0',
          exportType: 'project'
        }
      };

      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        const fileName = `${projectData.name.replace(/[^a-zA-Z0-9]/g, '_')}_project_export_${new Date().toISOString().split('T')[0]}.json`;
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error generating project JSON:', error);
      throw new Error('Failed to generate project JSON export');
    }
  }

  /**
   * Get project export options for display in UI
   */
  static getProjectExportOptions() {
    return [
      {
        id: 'pdf',
        label: 'Export as PDF',
        description: 'Comprehensive project report with visual elements',
        icon: 'FileText'
      },
      {
        id: 'csv',
        label: 'Export as CSV',
        description: 'Project data in spreadsheet-friendly format',
        icon: 'BarChart3'
      },
      {
        id: 'json',
        label: 'Export as JSON',
        description: 'Complete project data structure for API integration',
        icon: 'Code'
      }
    ];
  }

  /**
   * Export analysis data as PDF
   */
  static async exportToPDF(analysisData: AnalysisData): Promise<void> {
    try {
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let currentY = 20;

      // Helper function to add a new page if needed
      const checkPageBreak = (neededHeight: number) => {
        if (currentY + neededHeight > pageHeight - 20) {
          pdf.addPage();
          currentY = 20;
        }
      };

      // Helper function to wrap text
      const addWrappedText = (text: string, x: number, y: number, maxWidth: number, fontSize: number = 12) => {
        pdf.setFontSize(fontSize);
        const lines = pdf.splitTextToSize(text, maxWidth);
        pdf.text(lines, x, y);
        return y + (lines.length * fontSize * 0.35);
      };

      // Page 1: Cover Page
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Creative Analysis Report', pageWidth / 2, currentY, { align: 'center' });
      currentY += 15;

      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'normal');
      pdf.text(analysisData.creativeName, pageWidth / 2, currentY, { align: 'center' });
      currentY += 20;

      // Overall Score Circle
      const centerX = pageWidth / 2;
      const circleRadius = 30;
      pdf.setDrawColor(147, 51, 234); // Purple color
      pdf.setLineWidth(3);
      pdf.circle(centerX, currentY + circleRadius, circleRadius);
      
      pdf.setFontSize(28);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${analysisData.overallScore}%`, centerX, currentY + circleRadius + 5, { align: 'center' });
      
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Overall Score', centerX, currentY + circleRadius + 20, { align: 'center' });
      currentY += 80;

      // Analysis Details
      pdf.setFontSize(12);
      pdf.text(`Analysis Date: ${new Date(analysisData.analysisDate).toLocaleDateString()}`, 20, currentY);
      currentY += 8;
      pdf.text(`Version: ${analysisData.version}`, 20, currentY);
      currentY += 8;
      pdf.text(`Credits Used: ${analysisData.creditsUsed}`, 20, currentY);
      currentY += 8;
      pdf.text(`Creative Type: ${analysisData.creativeType.charAt(0).toUpperCase() + analysisData.creativeType.slice(1)}`, 20, currentY);
      currentY += 20;

      // Performance Summary
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Performance Summary', 20, currentY);
      currentY += 10;

      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Performance Score: ${analysisData.performanceAnalysis.overallPerformanceScore}%`, 20, currentY);
      currentY += 8;
      pdf.text(`Brand Awareness Score: ${analysisData.performanceAnalysis.brandAwarenessScore}%`, 20, currentY);
      currentY += 15;

      // Page 2: Platform Analysis
      pdf.addPage();
      currentY = 20;

      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Platform Analysis', 20, currentY);
      currentY += 15;

      Object.entries(analysisData.platforms).forEach(([platformName, platformData]) => {
        checkPageBreak(60);

        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text(platformName.charAt(0).toUpperCase() + platformName.slice(1), 20, currentY);
        currentY += 10;

        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Compliance Score: ${platformData.complianceScore}%`, 25, currentY);
        currentY += 6;
        pdf.text(`Performance Score: ${platformData.performanceScore}%`, 25, currentY);
        currentY += 6;
        pdf.text(`Predicted CTR: ${platformData.predictedCTR}%`, 25, currentY);
        currentY += 6;
        pdf.text(`Predicted Engagement: ${platformData.predictedEngagement}%`, 25, currentY);
        currentY += 6;
        pdf.text(`Algorithm Favorability: ${platformData.algorithmFavorability}%`, 25, currentY);
        currentY += 10;

        // Issues
        if (platformData.issues && platformData.issues.length > 0) {
          pdf.setFont('helvetica', 'bold');
          pdf.text('Issues:', 25, currentY);
          currentY += 6;

          platformData.issues.forEach((issue) => {
            checkPageBreak(20);
            pdf.setFont('helvetica', 'normal');
            pdf.text(`• ${issue.severity.toUpperCase()}: ${issue.title}`, 30, currentY);
            currentY += 6;
            currentY = addWrappedText(issue.description, 35, currentY, pageWidth - 50, 10);
            currentY += 3;
          });
        }
        currentY += 10;
      });

      // Page 3: Performance Analysis Details
      pdf.addPage();
      currentY = 20;

      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Performance Analysis', 20, currentY);
      currentY += 15;

      const keyFactors = analysisData.performanceAnalysis.keyFactors;
      Object.entries(keyFactors).forEach(([factorName, factorData]) => {
        checkPageBreak(40);

        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${factorName.charAt(0).toUpperCase() + factorName.slice(1)}: ${factorData.score}%`, 20, currentY);
        currentY += 8;

        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        currentY = addWrappedText(factorData.description, 25, currentY, pageWidth - 50);
        currentY += 5;

        if (factorData.details && factorData.details.length > 0) {
          factorData.details.forEach((detail) => {
            currentY = addWrappedText(`• ${detail}`, 30, currentY, pageWidth - 60, 10);
            currentY += 2;
          });
        }
        currentY += 10;
      });

      // Page 4: AI Recommendations
      pdf.addPage();
      currentY = 20;

      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('AI Recommendations', 20, currentY);
      currentY += 15;

      // Group recommendations by priority
      const groupedRecommendations = analysisData.aiRecommendations.reduce((groups, rec) => {
        if (!groups[rec.priority]) groups[rec.priority] = [];
        groups[rec.priority].push(rec);
        return groups;
      }, {} as Record<string, AIRecommendation[]>);

      ['high', 'medium', 'low'].forEach((priority) => {
        if (groupedRecommendations[priority]) {
          checkPageBreak(30);

          pdf.setFontSize(16);
          pdf.setFont('helvetica', 'bold');
          pdf.text(`${priority.charAt(0).toUpperCase() + priority.slice(1)} Priority`, 20, currentY);
          currentY += 10;

          groupedRecommendations[priority].forEach((rec) => {
            checkPageBreak(35);

            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'bold');
            pdf.text(`${rec.title} (${rec.category})`, 25, currentY);
            currentY += 6;

            pdf.setFont('helvetica', 'normal');
            currentY = addWrappedText(rec.description, 25, currentY, pageWidth - 50);
            currentY += 3;

            pdf.setFont('helvetica', 'italic');
            currentY = addWrappedText(`Impact: ${rec.impact}`, 25, currentY, pageWidth - 50, 10);
            currentY += 3;

            pdf.setFont('helvetica', 'normal');
            pdf.text(`Platforms: ${Array.isArray(rec.platforms) ? rec.platforms.join(', ') : rec.platforms}`, 25, currentY);
            currentY += 10;
          });
        }
      });

      // Footer on each page
      const totalPages = pdf.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Generated by Rulevia - ${new Date().toLocaleDateString()}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth - 20, pageHeight - 10, { align: 'right' });
      }

      // Generate filename
      const fileName = `${analysisData.creativeName.replace(/[^a-zA-Z0-9]/g, '_')}_analysis_${new Date().toISOString().split('T')[0]}.pdf`;
      
      // Save the PDF
      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw new Error('Failed to generate PDF report');
    }
  }

  /**
   * Export analysis data as CSV
   */
  static exportToCSV(analysisData: AnalysisData): void {
    try {
      const csvRows: string[] = [];
      
      // Headers
      csvRows.push('Creative Name,Analysis Date,Version,Overall Score,Performance Score,Brand Awareness Score');
      
      // Basic info row
      csvRows.push([
        `"${analysisData.creativeName || 'Unknown'}"`,
        `"${new Date(analysisData.analysisDate || new Date()).toLocaleDateString()}"`,
        `"${analysisData.version || '1.0'}"`,
        (analysisData.overallScore ?? 0).toString(),
        (analysisData.performanceAnalysis?.overallPerformanceScore ?? 0).toString(),
        (analysisData.performanceAnalysis?.brandAwarenessScore ?? 0).toString()
      ].join(','));

      // Empty row
      csvRows.push('');
      
      // Platform analysis headers
      csvRows.push('Platform,Compliance Score,Performance Score,Status,Predicted CTR,Predicted Engagement,Algorithm Favorability');
      
      // Platform data
      if (analysisData.platforms && typeof analysisData.platforms === 'object') {
        Object.entries(analysisData.platforms).forEach(([platformName, platformData]) => {
          if (platformData && typeof platformData === 'object') {
            csvRows.push([
              `"${platformName}"`,
              (platformData.complianceScore ?? 0).toString(),
              (platformData.performanceScore ?? 0).toString(),
              `"${platformData.status ?? 'Unknown'}"`,
              (platformData.predictedCTR ?? 0).toString(),
              (platformData.predictedEngagement ?? 0).toString(),
              (platformData.algorithmFavorability ?? 0).toString()
            ].join(','));
          }
        });
      }

      // Empty row
      csvRows.push('');
      
      // Issues section
      csvRows.push('Platform,Issue Severity,Issue Title,Issue Description');
      if (analysisData.platforms && typeof analysisData.platforms === 'object') {
        Object.entries(analysisData.platforms).forEach(([platformName, platformData]) => {
          if (platformData && typeof platformData === 'object' && platformData.issues && Array.isArray(platformData.issues) && platformData.issues.length > 0) {
            platformData.issues.forEach((issue) => {
              csvRows.push([
                `"${platformName}"`,
                `"${issue?.severity || 'unknown'}"`,
                `"${issue?.title || 'No title'}"`,
                `"${(issue?.description || '').replace(/"/g, '""')}"`
              ].join(','));
            });
          }
        });
      }

      // Empty row
      csvRows.push('');
      
      // Performance factors
      csvRows.push('Performance Factor,Score,Description');
      if (analysisData.performanceAnalysis?.keyFactors) {
        Object.entries(analysisData.performanceAnalysis.keyFactors).forEach(([factorName, factorData]) => {
          csvRows.push([
            `"${factorName || 'Unknown'}"`,
            (factorData?.score ?? 0).toString(),
            `"${(factorData?.description || '').replace(/"/g, '""')}"`
          ].join(','));
        });
      }

      // Empty row
      csvRows.push('');
      
      // AI Recommendations
      csvRows.push('Priority,Category,Title,Description,Impact,Platforms');
      if (analysisData.aiRecommendations && Array.isArray(analysisData.aiRecommendations)) {
        analysisData.aiRecommendations.forEach((rec) => {
          csvRows.push([
            `"${rec?.priority || 'low'}"`,
            `"${rec?.category || 'General'}"`,
            `"${rec?.title || 'No title'}"`,
            `"${(rec?.description || '').replace(/"/g, '""')}"`,
            `"${(rec?.impact || '').replace(/"/g, '""')}"`,
            `"${Array.isArray(rec?.platforms) ? rec.platforms.join(', ') : (rec?.platforms || 'All')}"`
          ].join(','));
        });
      }

      // Create and download CSV
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        const fileName = `${analysisData.creativeName.replace(/[^a-zA-Z0-9]/g, '_')}_analysis_${new Date().toISOString().split('T')[0]}.csv`;
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error generating CSV:', error);
      throw new Error('Failed to generate CSV export');
    }
  }

  /**
   * Export analysis data as JSON
   */
  static exportToJSON(analysisData: AnalysisData): void {
    try {
      // Create a clean JSON structure
      const exportData = {
        ...analysisData,
        exportMetadata: {
          exportDate: new Date().toISOString(),
          exportedBy: 'Rulevia Analysis Platform',
          version: '1.0'
        }
      };

      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        const fileName = `${analysisData.creativeName.replace(/[^a-zA-Z0-9]/g, '_')}_analysis_${new Date().toISOString().split('T')[0]}.json`;
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error generating JSON:', error);
      throw new Error('Failed to generate JSON export');
    }
  }

  /**
   * Get export options for display in UI
   */
  static getExportOptions() {
    return [
      {
        id: 'pdf',
        label: 'Export as PDF',
        description: 'Professional report with visual elements',
        icon: 'FileText'
      },
      {
        id: 'csv',
        label: 'Export as CSV',
        description: 'Spreadsheet-friendly format',
        icon: 'BarChart3'
      },
      {
        id: 'json',
        label: 'Export as JSON',
        description: 'Complete data structure for developers',
        icon: 'Code'
      }
    ];
  }
}